<template>
  <!-- squelette : garde la place de la carte pendant le chargement (le fil ne saute plus) -->
  <article
    v-if="isSessionPendingRef || isGroupRolesPendingRef"
    class="message-session-widget-skeleton"
    aria-busy="true"
    :aria-label="t('chat.session.loading')"
    data-test-id="message-session-widget-skeleton"
  >
    <div class="flex gap-3">
      <Skeleton class="size-10 rounded-lg" />
      <div class="flex flex-1 flex-col gap-2">
        <Skeleton class="h-3.5 w-2/3" />
        <Skeleton class="h-3 w-1/2" />
      </div>
    </div>
    <Skeleton class="h-3 w-5/6" />
    <Skeleton class="h-9 w-2/5 rounded-md" />
  </article>

  <article
    v-else-if="sessionRef"
    :class="[
      'message-session-widget',
      `message-session-widget--${sessionStatusClassNamesMap[sessionRef.status]}`,
      { 'message-session-widget--cancelled': cardStateRef === 'cancelled' },
    ]"
    :data-state="cardStateRef"
    data-test-id="message-session-widget"
  >
    <div class="message-session-widget__head">
      <span class="message-session-widget__icon" aria-hidden="true">
        <CalendarDays class="size-5" />
      </span>
      <div class="message-session-widget__titles">
        <p v-if="sessionRef.name" class="message-session-widget__eyebrow">{{ sessionRef.name }}</p>
        <p class="message-session-widget__title" data-test-id="message-session-widget-date">
          <time :datetime="sessionRef.from">{{ dateLabelRef }}</time>
        </p>
        <p class="message-session-widget__meta">
          <Video v-if="isOnlineRef" class="size-4 shrink-0" aria-hidden="true" />
          <MapPin v-else class="size-4 shrink-0" aria-hidden="true" />
          <span>{{ metaLabelRef }}</span>
        </p>
      </div>
      <span
        :class="['message-session-widget__status', `message-session-widget__status--${statusToneRef}`]"
        data-test-id="message-session-widget-status"
      >
        {{ t(`chat.session.status.${cardStateRef}`) }}
      </span>
    </div>

    <p
      v-if="sessionRef.objective && cardStateRef !== 'cancelled'"
      class="message-session-widget__objective"
      data-test-id="message-session-widget-objective"
    >
      <span class="text-muted-foreground">
        {{ t('chat.conversation.session.objective', { number: sessionRef.objective.order + 1 }) }} :
      </span>
      {{ sessionRef.objective.title }}
    </p>

    <div class="message-session-widget__participants">
      <div class="message-session-widget__avatars" :aria-label="participantsLabelRef" role="img">
        <span v-for="user in participantsRef" :key="user.id" class="message-session-widget__avatar" aria-hidden="true">
          {{ `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}` }}
        </span>
      </div>
      <p v-if="hintRef" class="message-session-widget__hint" data-test-id="message-session-widget-hint">
        <component :is="hintRef.icon" class="size-3.5 shrink-0" aria-hidden="true" />
        {{ hintRef.text }}
      </p>
    </div>

    <!-- demande a confirmer : le mentore accepte ou refuse -->
    <div v-if="canAnswerRef" class="message-session-widget__actions">
      <Button
        type="button"
        size="sm"
        :disabled="isUpdateSessionStatusPendingRef"
        :aria-busy="isUpdateSessionStatusPendingRef || undefined"
        @click="acceptOrDecline(true)"
      >
        <Loader2 v-if="isUpdateSessionStatusPendingRef" class="size-4 animate-spin" aria-hidden="true" />
        {{ t('ui.button.accept') }}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        :disabled="isUpdateSessionStatusPendingRef"
        @click="acceptOrDecline(false)"
      >
        {{ t('ui.button.decline') }}
      </Button>
    </div>

    <!-- demande envoyee : on peut modifier la proposition -->
    <div v-else-if="cardStateRef === 'toConfirm' && isProposerRef" class="message-session-widget__actions">
      <Button type="button" size="sm" variant="ghost" data-test-id="message-session-widget-edit" @click="openEditModal">
        <Pencil class="size-4" aria-hidden="true" />
        {{ t('chat.session.edit') }}
      </Button>
    </div>

    <!-- planifiee : rejoindre en premier quand la visio approche -->
    <div v-else-if="cardStateRef === 'planned' || cardStateRef === 'ongoing'" class="message-session-widget__actions">
      <Button v-if="canJoinRef" type="button" size="sm" data-test-id="message-session-widget-join" @click="joinSession">
        <Video class="size-4" aria-hidden="true" />
        {{ t('chat.session.join') }}
      </Button>
      <Button
        type="button"
        size="sm"
        :variant="canJoinRef ? 'ghost' : 'outline'"
        data-test-id="message-session-widget-calendar"
        @click="openCalendarModal"
      >
        <CalendarPlus v-if="!canJoinRef" class="size-4" aria-hidden="true" />
        {{ t('chat.session.addToCalendar') }}
      </Button>
    </div>

    <!-- apres la session : la carte mene au compte rendu -->
    <div v-else-if="cardStateRef === 'toComplete'" class="message-session-widget__actions">
      <Button type="button" size="sm" data-test-id="message-session-widget-report" @click="openSessionDetails">
        <Pencil class="size-4" aria-hidden="true" />
        {{ t('chat.session.writeReport') }}
      </Button>
    </div>
    <div v-else-if="cardStateRef === 'finished'" class="message-session-widget__actions">
      <Button
        type="button"
        size="sm"
        variant="outline"
        data-test-id="message-session-widget-view-report"
        @click="openSessionDetails"
      >
        {{ t('chat.session.viewReport') }}
      </Button>
    </div>
  </article>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { CalendarDays, CalendarPlus, Clock, Loader2, MapPin, Pencil, Video, XCircle } from 'lucide-vue-next'
import { computed, onBeforeUnmount, ref } from 'vue'
import { toast } from 'vue-sonner'

import {
  getSessionCardState,
  getSessionDurationParts,
  SESSION_JOIN_WINDOW_HOURS,
  type SessionCardStateType,
} from './session-card-state'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import { getMentees, getMentor } from '@/features/groups/utils'
import { V1UpdateSessionStatusMutation } from '@/features/sessions/api'
import useSession from '@/features/sessions/hooks/use-session.hook'
import { SessionStatusEnumType } from '@/features/sessions/types'
import { sessionStatusClassNamesMap } from '@/features/sessions/utils'
import type {
  AddToCalendarModalType,
  EditSessionModalType,
  SessionDetailsModalType,
} from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import useRouter from '@/hooks/use-router.hook'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'
import { invalidateSession, invalidateSessions } from '@/utils/lib/vue-query'

const props = defineProps<{
  sessionId: string
}>()

const { t, locale } = useI18n()
const router = useRouter()
const { accountRef } = useAccount()

const sessionIdRef = computed(() => props.sessionId)
const { sessionRef, isPendingRef: isSessionPendingRef } = useSession(sessionIdRef)
const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId)
const { groupRolesRef, isPendingRef: isGroupRolesPendingRef } = useGroupRoles({ groupIdRef })

// l'heure avance : la carte passe d'elle-meme a « imminente » puis « en cours »
const nowRef = ref(Date.now())
const timer = window.setInterval(() => (nowRef.value = Date.now()), 30_000)
onBeforeUnmount(() => window.clearInterval(timer))

const myStatusRef = computed(
  () => sessionRef.value?.users.find((user) => user.id === accountRef.value?.id)?.sessionStatus,
)
const isMentorRef = computed(() => !!groupRolesRef.value?.includes(RoleEnumTypeV2.MENTOR))
const isMenteeRef = computed(() => !!groupRolesRef.value?.includes(RoleEnumTypeV2.MENTEE))

const cardStateRef = computed<SessionCardStateType>(() =>
  sessionRef.value
    ? getSessionCardState({
        status: sessionRef.value.status,
        myStatus: myStatusRef.value,
        from: sessionRef.value.from,
        to: sessionRef.value.to,
        now: nowRef.value,
        othersWaiting: sessionRef.value.users.some(
          (user) => user.id !== accountRef.value?.id && user.sessionStatus === SessionStatusEnumType.WAIT_CONFIRMATION,
        ),
      })
    : 'planned',
)

const statusToneRef = computed(() => {
  switch (cardStateRef.value) {
    case 'toConfirm':
    case 'toComplete':
      return 'warning'
    case 'ongoing':
    case 'finished':
      return 'success'
    case 'cancelled':
    case 'waitReviews':
      return 'muted'
    default:
      return 'accent'
  }
})

const isOnlineRef = computed(() => sessionRef.value?.location === 'visio')

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)
// formats dayjs en dur par langue : les traductions passent par un traitement
// qui met une majuscule en tete, ce qui casserait un motif comme « dddd »
const formatsRef = computed(() =>
  String(locale.value).startsWith('fr')
    ? { date: 'dddd D MMMM, HH[\u00a0h\u00a0]mm', time: 'HH[\u00a0h\u00a0]mm' } // espaces insecables : l'heure ne se coupe pas en mobile
    : { date: 'dddd, MMMM D, h:mm A', time: 'h:mm A' },
)
const dateLabelRef = computed(() => {
  if (!sessionRef.value) return ''
  const from = dayjsClient(sessionRef.value.from)
  if (from.isSame(dayjsClient(nowRef.value), 'day')) {
    return t('chat.session.today', { time: from.format(formatsRef.value.time) })
  }
  return capitalize(from.format(formatsRef.value.date))
})

const durationLabelRef = computed(() => {
  if (!sessionRef.value) return ''
  const { hours, minutes } = getSessionDurationParts(sessionRef.value.from, sessionRef.value.to)
  if (!hours) return t('chat.session.duration.minutes', { count: minutes })
  if (!minutes) return t('chat.session.duration.hours', { hours })
  return t('chat.session.duration.hoursMinutes', { hours, minutes })
})

const metaLabelRef = computed(() =>
  isOnlineRef.value
    ? t('chat.session.metaOnline', { duration: durationLabelRef.value })
    : t('chat.session.metaPlace', { duration: durationLabelRef.value, place: sessionRef.value?.location ?? '' }),
)

const participantsRef = computed(() => {
  const users = sessionRef.value?.users ?? []
  const mentor = users.find(getMentor)
  return [...(mentor ? [mentor] : []), ...users.filter(getMentees)]
})
const participantsLabelRef = computed(() =>
  participantsRef.value.map((user) => `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()).join(', '),
)

// le mentore repond a la proposition
const canAnswerRef = computed(
  () =>
    isMenteeRef.value &&
    sessionRef.value?.status === SessionStatusEnumType.WAIT_CONFIRMATION &&
    myStatusRef.value === SessionStatusEnumType.WAIT_CONFIRMATION,
)
// seule la personne qui a reserve peut modifier sa proposition depuis la carte ;
// pour une session anterieure (sans auteur connu), on retombe sur le mentor qui l'a planifiee
const isProposerRef = computed(() => {
  const createdBy = sessionRef.value?.createdBy
  if (createdBy) return createdBy === accountRef.value?.id
  return isMentorRef.value && myStatusRef.value === SessionStatusEnumType.PLANNED
})

// rejoindre : meme regle que le panneau de contexte, 2 h avant le debut et pendant la session
const canJoinRef = computed(() => {
  if (!sessionRef.value || !isOnlineRef.value) return false
  const start = new Date(sessionRef.value.from).getTime()
  const end = new Date(sessionRef.value.to).getTime()
  return nowRef.value < end && start - nowRef.value <= SESSION_JOIN_WINDOW_HOURS * 3_600_000
})

// ligne d'information a droite des avatars : qui doit repondre, quand ca commence, etc.
const hintRef = computed(() => {
  const session = sessionRef.value
  if (!session) return null
  switch (cardStateRef.value) {
    case 'toConfirm': {
      if (canAnswerRef.value) return null
      const waiting = session.users
        .filter((user) => user.id !== accountRef.value?.id)
        .filter((user) => user.sessionStatus === SessionStatusEnumType.WAIT_CONFIRMATION)
        .map((user) => user.firstName)
        .filter(Boolean)
      return waiting.length ? { icon: Clock, text: t('chat.session.waitingFor', { names: waiting.join(', ') }) } : null
    }
    case 'planned': {
      if (!canJoinRef.value) return null
      const minutes = Math.max(1, Math.round((new Date(session.from).getTime() - nowRef.value) / 60_000))
      return minutes < 60
        ? { icon: Clock, text: t('chat.session.startsIn', { count: minutes }) }
        : { icon: Clock, text: t('chat.session.startsInHours', { count: Math.round(minutes / 60) }) }
    }
    case 'ongoing':
      return {
        icon: Clock,
        text: t('chat.session.endsAt', { time: dayjsClient(session.to).format(formatsRef.value.time) }),
      }
    case 'toComplete':
      return { icon: Pencil, text: t('chat.session.reportExpected') }
    case 'cancelled':
      return {
        icon: XCircle,
        text: t('chat.session.cancelledOn', { date: dayjsClient(session.updatedAt).format('D MMMM') }),
      }
    default:
      return null
  }
})

// update session
const { mutate: updateSessionStatus, isPending: isUpdateSessionStatusPendingRef } = useMutation({
  mutationFn: V1UpdateSessionStatusMutation,
  onError: (error) => {
    console.error(error)
    toast?.error(t('error.common'))
  },
  onSuccess: async (data, variables) => {
    await invalidateSessions()
    await invalidateSession(sessionIdRef.value)
    if (variables.status === SessionStatusEnumType.CANCELLED) {
      toast?.success(t('chat.conversation.session.declined'))
    } else {
      toast?.success(t('chat.conversation.session.accepted'))
    }
  },
})

// handle accept and decline
function acceptOrDecline(accept: boolean) {
  if (sessionRef.value) {
    updateSessionStatus({
      sessionId: sessionRef.value.id,
      status: accept ? SessionStatusEnumType.PLANNED : SessionStatusEnumType.CANCELLED,
    })
  }
}

function openCalendarModal() {
  modalsStore.toggleModal('add-to-calendar', true)
  modalsStore.updateModalAttachedData<AddToCalendarModalType>('add-to-calendar', {
    session: sessionRef.value!,
  })
}

function openEditModal() {
  modalsStore.toggleModal('edit-session', true)
  modalsStore.updateModalAttachedData<EditSessionModalType>('edit-session', {
    sessionId: props.sessionId,
  })
}

// le detail de la session porte le compte rendu (a rediger ou a lire)
function openSessionDetails() {
  modalsStore.updateModalAttachedData<SessionDetailsModalType>('session-details', {
    sessionId: props.sessionId,
  })
  modalsStore.toggleModal('session-details', true)
}

function joinSession() {
  if (!sessionRef.value) return
  router?.push({ query: { ...router.currentRoute.query, meetingRoomId: sessionRef.value.meetingRoomId } })
}
</script>

<style lang="scss">
@import './message-session-widget.scss';
</style>
